/**
 * Spotify Library Page
 * 
 * Browse Spotify playlists, saved tracks, albums, and search
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { spotifyAuth, spotifyAPI } from '../services/spotify';
import SpotifyPlayer from '../components/SpotifyPlayer';
import './SpotifyLibrary.css';

function SpotifyLibrary({ user }) {
  const navigate = useNavigate();
  
  // Auth state
  const [isConnected, setIsConnected] = useState(false);
  const [spotifyUser, setSpotifyUser] = useState(null);
  
  // Data state
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [playlistTracks, setPlaylistTracks] = useState([]);
  const [savedTracks, setSavedTracks] = useState([]);
  const [recentlyPlayed, setRecentlyPlayed] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [loadingTracks, setLoadingTracks] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [activeTab, setActiveTab] = useState('playlists');
  const [error, setError] = useState(null);
  
  // Player state
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audio, setAudio] = useState(null);
  const [queue, setQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(0);
  const [usePremiumPlayer, setUsePremiumPlayer] = useState(false);

  // Check connection and load data
  useEffect(() => {
    const initSpotify = async () => {
      if (!spotifyAuth.isAuthenticated()) {
        setIsConnected(false);
        setLoading(false);
        return;
      }

      setIsConnected(true);
      
      try {
        const [userProfile, playlistsData, tracksData, recentData] = await Promise.all([
          spotifyAuth.getUserProfile(),
          spotifyAPI.getMyPlaylists(50),
          spotifyAPI.getSavedTracks(20),
          spotifyAPI.getRecentlyPlayed(20)
        ]);

        setSpotifyUser(userProfile);
        setPlaylists(playlistsData.items);
        setSavedTracks(tracksData.items);
        setRecentlyPlayed(recentData.items);
        
        // Check if user has Premium for full playback
        if (userProfile.product === 'premium') {
          setUsePremiumPlayer(true);
        }
      } catch (err) {
        console.error('Error loading Spotify data:', err);
        setError(err.message);
        if (err.message.includes('expired')) {
          setIsConnected(false);
        }
      } finally {
        setLoading(false);
      }
    };

    initSpotify();
  }, []);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audio) {
        audio.pause();
        audio.src = '';
      }
    };
  }, [audio]);

  // Load playlist tracks
  const loadPlaylistTracks = useCallback(async (playlist) => {
    setLoadingTracks(true);
    setSelectedPlaylist(playlist);
    setPlaylistTracks([]);
    
    try {
      const data = await spotifyAPI.getPlaylistTracks(playlist.id);
      setPlaylistTracks(data.items);
    } catch (err) {
      console.error('Error loading playlist tracks:', err);
      setError(err.message);
    } finally {
      setLoadingTracks(false);
    }
  }, []);

  // Search
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setSearching(true);
    setSearchResults([]);
    setActiveTab('search');

    try {
      const data = await spotifyAPI.search(searchQuery, ['track'], 30);
      setSearchResults(data.tracks || []);
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message);
    } finally {
      setSearching(false);
    }
  };

  // Play a track
  const playTrack = (track, trackList = null) => {
    // If using Premium player, just set the track
    if (usePremiumPlayer) {
      setCurrentTrack(track);
      if (trackList) {
        setQueue(trackList);
        setQueueIndex(trackList.findIndex(t => t.id === track.id));
      }
      setIsPlaying(true);
      return;
    }

    // Stop current audio (for preview playback)
    if (audio) {
      audio.pause();
    }

    if (!track.previewUrl) {
      // Open in Spotify if no preview
      window.open(`https://open.spotify.com/track/${track.id}`, '_blank');
      return;
    }

    const newAudio = new Audio(track.previewUrl);
    newAudio.play();
    setAudio(newAudio);
    setCurrentTrack(track);
    setIsPlaying(true);

    if (trackList) {
      setQueue(trackList);
      setQueueIndex(trackList.findIndex(t => t.id === track.id));
    }

    newAudio.onended = () => {
      // Auto-play next if in queue
      handleNext();
    };
  };

  // Next track
  const handleNext = () => {
    if (queueIndex < queue.length - 1) {
      const nextIndex = queueIndex + 1;
      setQueueIndex(nextIndex);
      playTrack(queue[nextIndex]);
    } else {
      setIsPlaying(false);
      setCurrentTrack(null);
    }
  };

  // Previous track
  const handlePrevious = () => {
    if (queueIndex > 0) {
      const prevIndex = queueIndex - 1;
      setQueueIndex(prevIndex);
      playTrack(queue[prevIndex]);
    }
  };

  // Pause current track
  const pauseTrack = () => {
    if (audio) {
      audio.pause();
      setIsPlaying(false);
    }
  };

  // Resume current track
  const resumeTrack = () => {
    if (audio) {
      audio.play();
      setIsPlaying(true);
    }
  };

  // Open in Spotify
  const openInSpotify = (uri) => {
    window.open(uri.replace('spotify:', 'https://open.spotify.com/').replace(/:/g, '/'), '_blank');
  };

  // Format duration
  const formatDuration = (ms) => {
    if (!ms) return '0:00';
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Connect Spotify
  const handleConnect = async () => {
    try {
      await spotifyAuth.signIn();
    } catch (err) {
      setError(err.message);
    }
  };

  // Not connected view
  if (!isConnected && !loading) {
    return (
      <div className="spotify-library">
        <div className="spotify-not-connected">
          <div className="not-connected-content">
            <div className="spotify-logo">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
              </svg>
            </div>
            <h2>Connect Spotify</h2>
            <p>Sign in with your Spotify account to access your playlists and music</p>
            <button className="connect-btn" onClick={handleConnect}>
              Connect Spotify Account
            </button>
            <button className="back-btn" onClick={() => navigate('/services')}>
              ← Back to Services
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading view
  if (loading) {
    return (
      <div className="spotify-library">
        <div className="spotify-loading">
          <div className="loading-spinner">🎵</div>
          <p>Loading your Spotify library...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="spotify-library">
      {/* Header */}
      <header className="spotify-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/services')}>
            ← Back
          </button>
          <div className="spotify-branding">
            <svg viewBox="0 0 24 24" fill="currentColor" className="spotify-icon">
              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
            </svg>
            <h1>Spotify Library</h1>
          </div>
        </div>
        
        <div className="header-right">
          {spotifyUser && (
            <div className="user-info">
              {spotifyUser.image && (
                <img src={spotifyUser.image} alt="" className="user-avatar" />
              )}
              <span>{spotifyUser.name}</span>
              {spotifyUser.product === 'premium' && (
                <span className="premium-badge">Premium</span>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Error */}
      {error && (
        <div className="spotify-error">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Search Bar */}
      <div className="spotify-search-bar">
        <form onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Search Spotify..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button type="submit" disabled={searching}>
            {searching ? '...' : '🔍'}
          </button>
        </form>
      </div>

      {/* Tabs */}
      <div className="spotify-tabs">
        <button
          className={`tab ${activeTab === 'playlists' ? 'active' : ''}`}
          onClick={() => setActiveTab('playlists')}
        >
          📁 Playlists ({playlists.length})
        </button>
        <button
          className={`tab ${activeTab === 'saved' ? 'active' : ''}`}
          onClick={() => setActiveTab('saved')}
        >
          💚 Liked Songs ({savedTracks.length})
        </button>
        <button
          className={`tab ${activeTab === 'recent' ? 'active' : ''}`}
          onClick={() => setActiveTab('recent')}
        >
          🕐 Recently Played
        </button>
        {searchResults.length > 0 && (
          <button
            className={`tab ${activeTab === 'search' ? 'active' : ''}`}
            onClick={() => setActiveTab('search')}
          >
            🔍 Search ({searchResults.length})
          </button>
        )}
      </div>

      {/* Main Content */}
      <div className="spotify-content">
        {/* Sidebar */}
        <div className="spotify-sidebar">
          {activeTab === 'playlists' && (
            <>
              <h3>Your Playlists</h3>
              <div className="playlist-list">
                {playlists.map(playlist => (
                  <div
                    key={playlist.id}
                    className={`playlist-item ${selectedPlaylist?.id === playlist.id ? 'selected' : ''}`}
                    onClick={() => loadPlaylistTracks(playlist)}
                  >
                    <img 
                      src={playlist.image || '/placeholder-playlist.png'} 
                      alt="" 
                      className="playlist-thumb"
                    />
                    <div className="playlist-info">
                      <span className="playlist-name">{playlist.name}</span>
                      <span className="playlist-count">{playlist.trackCount} tracks</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {activeTab === 'saved' && (
            <>
              <h3>Liked Songs</h3>
              <div className="track-list">
                {savedTracks.map(track => (
                  <div
                    key={track.id}
                    className={`track-item ${currentTrack?.id === track.id ? 'playing' : ''}`}
                    onClick={() => playTrack(track)}
                  >
                    <img 
                      src={track.image || '/placeholder-track.png'} 
                      alt="" 
                      className="track-thumb"
                    />
                    <div className="track-info">
                      <span className="track-name">{track.name}</span>
                      <span className="track-artist">{track.artist}</span>
                    </div>
                    <span className="track-duration">{formatDuration(track.duration)}</span>
                    {currentTrack?.id === track.id && <span className="now-playing">🎵</span>}
                  </div>
                ))}
              </div>
            </>
          )}

          {activeTab === 'recent' && (
            <>
              <h3>Recently Played</h3>
              <div className="track-list">
                {recentlyPlayed.map((track, index) => (
                  <div
                    key={`${track.id}-${index}`}
                    className={`track-item ${currentTrack?.id === track.id ? 'playing' : ''}`}
                    onClick={() => playTrack(track)}
                  >
                    <img 
                      src={track.image || '/placeholder-track.png'} 
                      alt="" 
                      className="track-thumb"
                    />
                    <div className="track-info">
                      <span className="track-name">{track.name}</span>
                      <span className="track-artist">{track.artist}</span>
                    </div>
                    <span className="track-duration">{formatDuration(track.duration)}</span>
                    {currentTrack?.id === track.id && <span className="now-playing">🎵</span>}
                  </div>
                ))}
              </div>
            </>
          )}

          {activeTab === 'search' && (
            <>
              <h3>Search Results</h3>
              <div className="track-list">
                {searchResults.map(track => (
                  <div
                    key={track.id}
                    className={`track-item ${currentTrack?.id === track.id ? 'playing' : ''}`}
                    onClick={() => playTrack(track)}
                  >
                    <img 
                      src={track.image || '/placeholder-track.png'} 
                      alt="" 
                      className="track-thumb"
                    />
                    <div className="track-info">
                      <span className="track-name">{track.name}</span>
                      <span className="track-artist">{track.artist}</span>
                    </div>
                    <span className="track-duration">{formatDuration(track.duration)}</span>
                    {currentTrack?.id === track.id && <span className="now-playing">🎵</span>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Main Area */}
        <div className="spotify-main">
          {activeTab === 'playlists' && selectedPlaylist && (
            <div className="playlist-detail">
              <div className="playlist-header">
                <img 
                  src={selectedPlaylist.image} 
                  alt="" 
                  className="playlist-cover"
                />
                <div className="playlist-meta">
                  <h2>{selectedPlaylist.name}</h2>
                  <p>{selectedPlaylist.trackCount} tracks • {selectedPlaylist.owner}</p>
                  <button 
                    className="open-spotify-btn"
                    onClick={() => openInSpotify(selectedPlaylist.uri)}
                  >
                    Open in Spotify
                  </button>
                </div>
              </div>

              {loadingTracks ? (
                <div className="loading-tracks">Loading tracks...</div>
              ) : (
                <div className="track-list">
                  {playlistTracks.map((track, index) => (
                    <div
                      key={`${track.id}-${index}`}
                      className={`track-item ${currentTrack?.id === track.id ? 'playing' : ''}`}
                      onClick={() => playTrack(track)}
                    >
                      <span className="track-index">{index + 1}</span>
                      <img 
                        src={track.image || '/placeholder-track.png'} 
                        alt="" 
                        className="track-thumb"
                      />
                      <div className="track-info">
                        <span className="track-name">{track.name}</span>
                        <span className="track-artist">{track.artist}</span>
                      </div>
                      <span className="track-duration">{formatDuration(track.duration)}</span>
                      {currentTrack?.id === track.id && <span className="now-playing">🎵</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'playlists' && !selectedPlaylist && (
            <div className="no-selection">
              <p>👈 Select a playlist to view its tracks</p>
            </div>
          )}

          {(activeTab === 'saved' || activeTab === 'recent' || activeTab === 'search') && (
            <div className="info-panel">
              {/* Premium Player */}
              {usePremiumPlayer && currentTrack && (
                <SpotifyPlayer
                  trackUri={currentTrack.uri}
                  currentTrack={currentTrack}
                  onTrackEnd={handleNext}
                  onNext={queueIndex < queue.length - 1 ? handleNext : null}
                  onPrevious={queueIndex > 0 ? handlePrevious : null}
                  queueLength={queue.length}
                  currentIndex={queueIndex}
                />
              )}

              {/* Preview Player (for free users) */}
              {!usePremiumPlayer && (
                <div className="info-card">
                  <h3>🎧 Now Playing</h3>
                  {currentTrack ? (
                    <div className="now-playing-card">
                      <img src={currentTrack.image} alt="" className="now-playing-image" />
                      <div className="now-playing-info">
                        <p className="now-playing-title">{currentTrack.name}</p>
                        <p className="now-playing-artist">{currentTrack.artist}</p>
                      </div>
                      <div className="now-playing-controls">
                        {isPlaying ? (
                          <button onClick={pauseTrack} className="control-btn">⏸</button>
                        ) : (
                          <button onClick={resumeTrack} className="control-btn">▶</button>
                        )}
                        <button 
                          onClick={() => openInSpotify(currentTrack.uri)} 
                          className="control-btn spotify-btn"
                        >
                          Open in Spotify
                        </button>
                      </div>
                      <p className="preview-note">🔊 Playing 30-second preview</p>
                    </div>
                  ) : (
                    <p className="no-track">Select a track to play</p>
                  )}
                </div>
              )}

              {!usePremiumPlayer && (
                <div className="info-card">
                  <h3>ℹ️ Note</h3>
                  <p>Free Spotify accounts can only play 30-second previews. For full playback, open tracks in the Spotify app.</p>
                </div>
              )}

              {usePremiumPlayer && !currentTrack && (
                <div className="info-card">
                  <h3>👑 Premium Playback Ready</h3>
                  <p>Select a track to start full playback!</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SpotifyLibrary;

